// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { toast } from "sonner";

// ── Module-level debug flag ──────────────────────────────────────────────────

let _debugMode = false;
export function isDebugModeEnabled(): boolean {
  return _debugMode;
}

// ── Telemetry store ──────────────────────────────────────────────────────────

export interface TelemetryRecord {
  id: string;
  method: string;
  pathname: string;
  search: string;
  status: number;
  duration: number | null;
  timestamp: number;
}

let _records: TelemetryRecord[] = [];
const _listeners = new Set<() => void>();

function notifyListeners() {
  _listeners.forEach((l) => l());
}

export function addTelemetryRecord(record: Omit<TelemetryRecord, "id" | "timestamp">): void {
  _records = [{ ...record, id: crypto.randomUUID(), timestamp: Date.now() }, ..._records].slice(0, 100);
  notifyListeners();
}

export function clearTelemetry(): void {
  _records = [];
  notifyListeners();
}

export function useTelemetry(): TelemetryRecord[] {
  return useSyncExternalStore(
    (cb) => {
      _listeners.add(cb);
      return () => _listeners.delete(cb);
    },
    () => _records,
  );
}

// ── Debug mode context ───────────────────────────────────────────────────────

const CLICKS_REQUIRED = 5;
const RESET_MS = 2000;

interface DebugModeContextValue {
  debugMode: boolean;
  onLogoClick: () => void;
}

const DebugModeContext = createContext<DebugModeContextValue>({
  debugMode: false,
  onLogoClick: () => {},
});

const KONAMI = [
  "ArrowUp",
  "ArrowUp",
  "ArrowDown",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowLeft",
  "ArrowRight",
  "b",
  "a",
];

export function DebugModeProvider({ children, isAdmin = false }: { children: ReactNode; isAdmin?: boolean }) {
  const [rawDebugMode, setRawDebugMode] = useState(() => sessionStorage.getItem("debug_mode") === "1");
  const prevIsAdmin = useRef(isAdmin);
  const debugMode = rawDebugMode && isAdmin;

  // Reset debug mode when admin status is revoked (logout or role change)
  useEffect(
    function resetDebugModeOnAdminRevoke() {
      if (prevIsAdmin.current && !isAdmin && rawDebugMode) {
        setRawDebugMode(false);
      }
      prevIsAdmin.current = isAdmin;
    },
    [isAdmin, rawDebugMode],
  );

  const enableDebug = useCallback(() => {
    if (!isAdmin) return;
    setRawDebugMode((prev) => {
      if (!prev)
        toast(
          <span>
            You're in sensei — <b>DEBUG ENABLED</b>
          </span>,
        );
      return !prev;
    });
  }, [isAdmin]);

  useEffect(
    function syncDebugModeToSessionStorage() {
      _debugMode = debugMode;
      if (rawDebugMode) {
        sessionStorage.setItem("debug_mode", "1");
      } else {
        sessionStorage.removeItem("debug_mode");
        clearTelemetry();
      }
    },
    [debugMode, rawDebugMode],
  );

  // Konami code listener
  const konamiProgress = useRef(0);
  useEffect(
    function registerKonamiCodeListener() {
      function handleKeyDown(e: KeyboardEvent) {
        if (e.key === KONAMI[konamiProgress.current]) {
          konamiProgress.current += 1;
          if (konamiProgress.current === KONAMI.length) {
            konamiProgress.current = 0;
            enableDebug();
          }
        } else {
          konamiProgress.current = e.key === KONAMI[0] ? 1 : 0;
        }
      }
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    },
    [enableDebug],
  );

  const clickCount = useRef(0);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onLogoClick = useCallback(() => {
    clickCount.current += 1;

    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => {
      clickCount.current = 0;
    }, RESET_MS);

    if (clickCount.current >= CLICKS_REQUIRED) {
      clickCount.current = 0;
      if (resetTimer.current) clearTimeout(resetTimer.current);
      enableDebug();
    }
  }, [enableDebug]);

  return <DebugModeContext.Provider value={{ debugMode, onLogoClick }}>{children}</DebugModeContext.Provider>;
}

export function useDebugMode(): DebugModeContextValue {
  return useContext(DebugModeContext);
}
